// src/services/customerService.js
import apiClient from './api';
import logger from '../utils/logger';

const isSuccessStatus = (status) => status >= 200 && status < 300;

const describeCustomerError = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (typeof data.error === 'string') return data.error;
  if (typeof data.detail === 'string') return data.detail;
  const messages = Object.values(data).flatMap((value) => (Array.isArray(value) ? value : [String(value)]));
  return messages.length ? messages.join(' ') : fallback;
};

export const customerService = {
  getCustomerById: async (customerId, options = {}) => {
    try {
      const params = {};
      if (options.includePermissions) {
        params.include_permissions = 'true';
      }
      const response = await apiClient.get(`/customers/${customerId}/`, { params });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch customer'
      };
    }
  },

  getCustomerIds: async () => {
    try {
      const response = await apiClient.get('/customers/ids/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch customer IDs'
      };
    }
  },

  getCustomerProfile: async () => {
    logger.log('[CustomerService] getCustomerProfile called');
    try {
      const response = await apiClient.get('/customers/me/');
      logger.log('[CustomerService] API response:', response.data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      logger.log('[CustomerService] API error:', error.response?.status, error.response?.data);
      // If it's a 403 or other specific error, return the error details
      if (error.response?.status === 403) {
        return {
          success: false,
          error: error.response?.data?.error || 'Access denied'
        };
      }
      // For other errors, return the error details but don't consider it a failure
      // since the new API returns 200 with capability info even for users without customer accounts
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch customer profile'
      };
    }
  },
  
  getCustomers: async (params = {}) => {
    try {
      // Default pagination parameters for better performance  
      const defaultParams = {
        page: 1,
        page_size: 50, // Reasonable page size for good performance
        ...params
      };
      
      const queryParams = new URLSearchParams();
      Object.entries(defaultParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      
      const url = `/customers/?${queryParams.toString()}`;
      const response = await apiClient.get(url);
      
      // Debug logging with performance info
      const searchInfo = params.search ? ` (search: "${params.search}")` : '';
      const resultCount = response.data.results?.length || 0;
      const totalCount = response.data.count || 0;
      logger.log(`[CustomerService] Loaded page ${defaultParams.page}: ${resultCount} customers of ${totalCount} total${searchInfo}`);
      
      // Handle paginated response
      if (response.data.results) {
        return {
          success: true,
          data: {
            results: response.data.results,
            count: response.data.count,
            next: response.data.next,
            previous: response.data.previous,
            currentPage: defaultParams.page,
            pageSize: defaultParams.page_size,
            totalPages: Math.ceil(response.data.count / defaultParams.page_size),
            searchTerm: params.search || '',
            hasMore: !!response.data.next
          }
        };
      }
      
      // Handle direct array response (backwards compatibility)
      const customerData = Array.isArray(response.data) ? response.data : [];
      logger.log('[CustomerService] Processed customer data (direct array):', customerData.length);
      
      return {
        success: true,
        data: customerData
      };
    } catch (error) {
      console.error('[CustomerService] Customer API error:', error);
      console.error('[CustomerService] Error response:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch customers'
      };
    }
  },

  // New optimized search method for autocomplete/selectors
  searchCustomers: async (searchTerm, limit = 50) => {
    try {
      if (!searchTerm || searchTerm.length < 2) {
        return {
          success: true,
          data: []
        };
      }

      logger.log(`[CustomerService] Searching customers: "${searchTerm}"`);
      
      const result = await customerService.getCustomers({
        search: searchTerm,
        page_size: limit,
        ordering: 'name' // Order by name for better UX
      });

      if (result.success) {
        const customers = result.data?.results || result.data || [];
        logger.log(`[CustomerService] Search completed: ${customers.length} results`);
        return {
          success: true,
          data: customers
        };
      }

      return result;
    } catch (error) {
      console.error('[CustomerService] Search error:', error);
      return {
        success: false,
        error: 'Search failed'
      };
    }
  },

  
  getCustomersMapData: async (categoryId = null, options = {}) => {
    try {
      logger.log('[CustomerService] Fetching map data for category:', categoryId || 'all');
      
      const params = {
        // Add pagination support for large datasets
        page_size: options.pageSize || 500, // Reasonable limit for map display
        page: options.page || 1
      };
      
      if (categoryId) {
        params.category_id = categoryId;
      }
      
      // Add bounds-based filtering if provided (for viewport-based loading)
      if (options.bounds) {
        params.lat_min = options.bounds.south;
        params.lat_max = options.bounds.north;
        params.lng_min = options.bounds.west;
        params.lng_max = options.bounds.east;
      }
      
      const response = await apiClient.get('/customers/map-data/', {
        params,
        timeout: 15000 // 15 second timeout for map data
      });
      
      const resultCount = response.data.results?.length || response.data.length;
      logger.log(`[CustomerService] Map data loaded: ${resultCount} customers`);
      
      // Handle paginated response
      if (response.data.results) {
        return {
          success: true,
          data: response.data.results,
          count: response.data.count,
          hasMore: !!response.data.next
        };
      }
      
      // Handle direct array response (backwards compatibility)
      return {
        success: true,
        data: response.data,
        count: response.data.length,
        hasMore: false
      };
    } catch (error) {
      console.error('[CustomerService] Map data error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch map data'
      };
    }
  },

  getCustomersPaginated: async (page = 1, pageSize = 20) => {
    try {
      const response = await apiClient.get('/customers/', {
        params: {
          page,
          page_size: pageSize
        }
      });
      
      return {
        success: true,
        data: response.data.results || response.data, // Handle both paginated and non-paginated responses
        count: response.data.count || response.data.length,
        next: response.data.next,
        previous: response.data.previous
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch customers'
      };
    }
  },
  
  // Add the method to update customer user
  updateCustomerUser: async (customerId, userData) => {
    try {
      const response = await apiClient.patch(`/customers/${customerId}/update-user/`, {
        user: userData
      });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update user details'
      };
    }
  },

  createCustomer: async (customerData) => {
    try {
      const response = await apiClient.post('/customers/create/', customerData);
      if (!isSuccessStatus(response.status)) {
        return {
          success: false,
          error: response.data?.error || response.data || 'Failed to create customer'
        };
      }
      return { success: true, data: response.data.data || response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data || 'Failed to create customer'
      };
    }
  },

  createCustomerLocation: async (customerId) => {
    try {
      logger.log(`[CustomerService] Creating location for customer ${customerId}`);
      const response = await apiClient.post('/customers/locations/create/', {
        customer_id: customerId
      });
      
      logger.log('[CustomerService] Location created successfully:', response.data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[CustomerService] Error creating location:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create location'
      };
    }
  },

  getCustomerLocation: async (customerId) => {
    try {
      logger.log(`[CustomerService] Fetching location for customer ${customerId}`);
      const response = await apiClient.get(`/customers/${customerId}/location/`);
      
      logger.log('[CustomerService] Location fetched successfully:', response.data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[CustomerService] Error fetching location:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch location'
      };
    }
  },

  // Batch location loading - reduces N+1 queries for multiple customers
  getCustomerLocationsBatch: async (customerIds) => {
    try {
      if (!Array.isArray(customerIds) || customerIds.length === 0) {
        return {
          success: true,
          data: {}
        };
      }

      // Limit batch size for performance
      if (customerIds.length > 50) {
        logger.warn(`[CustomerService] Large batch size (${customerIds.length}), limiting to 50`);
        customerIds = customerIds.slice(0, 50);
      }

      const idsParam = customerIds.join(',');
      logger.log(`[CustomerService] Fetching batch locations for ${customerIds.length} customers`);

      const response = await apiClient.get(`/customers/locations/?ids=${idsParam}`);

      logger.log('[CustomerService] Batch locations fetched successfully:', Object.keys(response.data).length, 'locations');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[CustomerService] Error fetching batch locations:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch batch locations'
      };
    }
  },

  updateCustomer: async (customerId, customerData) => {
    try {
      logger.log(`[CustomerService] Updating customer ${customerId}`);
      const response = await apiClient.patch(`/customers/${customerId}/update/`, customerData);
      if (!isSuccessStatus(response.status)) {
        return {
          success: false,
          status: response.status,
          error: describeCustomerError(response.data, 'Failed to update customer')
        };
      }

      logger.log('[CustomerService] Customer updated successfully');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[CustomerService] Error updating customer:', error.response?.data);
      return {
        success: false,
        status: error.response?.status,
        error: describeCustomerError(error.response?.data, 'Failed to update customer')
      };
    }
  },

  recordLicenceCheck: async (customerId, status) => {
    try {
      const response = await apiClient.post(`/customers/${customerId}/licence-check/`, { status });
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: describeCustomerError(error.response?.data, 'Failed to record the licence check') };
    }
  },

  uploadLicenceDocument: async (customerId, file) => {
    try {
      const body = new FormData();
      body.append('file', file);
      const response = await apiClient.post(`/customers/${customerId}/licence-document/`, body, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: describeCustomerError(error.response?.data, 'Failed to upload the licence copy') };
    }
  },

  deleteLicenceDocument: async (customerId) => {
    try {
      const response = await apiClient.delete(`/customers/${customerId}/licence-document/`);
      return { success: true, data: response.data.data };
    } catch (error) {
      return { success: false, error: describeCustomerError(error.response?.data, 'Failed to remove the licence copy') };
    }
  },

  openLicenceDocument: async (customerId) => {
    try {
      const response = await apiClient.get(`/customers/${customerId}/licence-document/`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to open the licence copy' };
    }
  },

  getFulfillmentOptions: async (customerId) => {
    try {
      logger.log(`[CustomerService] Fetching fulfillment options for customer ${customerId}`);
      const response = await apiClient.get(`/customers/${customerId}/fulfillment-options/`);

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[CustomerService] Error fetching fulfillment options:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch fulfillment options'
      };
    }
  },

  getAvailableDates: async (customerId, fulfillmentType = 'delivery', days = 30) => {
    try {
      logger.log(`[CustomerService] Fetching available dates for customer ${customerId}, type: ${fulfillmentType}`);
      const response = await apiClient.get(`/customers/${customerId}/available-dates/`, {
        params: {
          type: fulfillmentType,
          days: days
        }
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[CustomerService] Error fetching available dates:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch available dates'
      };
    }
  },

  getCreditStatus: async (customerId) => {
    try {
      logger.log(`[CustomerService] Fetching credit status for customer ${customerId}`);
      const response = await apiClient.get(`/customers/${customerId}/`, {
        params: {
          include_credit_status: 'true'
        }
      });

      return {
        success: true,
        data: response.data.credit_status
      };
    } catch (error) {
      console.error('[CustomerService] Error fetching credit status:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch credit status'
      };
    }
  },

  // Assignment management methods
  getAvailableSalesReps: async () => {
    try {
      logger.log('[CustomerService] Fetching available sales reps');
      const response = await apiClient.get('/customers/users/roles/salesrep/');
      logger.log('[CustomerService] Available sales reps:', response.data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[CustomerService] Error fetching sales reps:', error.response?.status, error.response?.data);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch sales reps'
      };
    }
  },

  getAvailableCustomerManagers: async () => {
    try {
      const response = await apiClient.get('/customers/users/roles/customermanager/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch customer managers'
      };
    }
  },

  assignSalesRep: async (customerId, salesRepId) => {
    try {
      logger.log('[CustomerService] Assigning sales rep:', { customerId, salesRepId });
      const response = await apiClient.post('/customers/assignments/sales/create/', {
        sales_rep_id: salesRepId,
        customer_ids: [customerId]
      });
      logger.log('[CustomerService] Assignment response:', response.data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[CustomerService] Assignment error:', error.response?.status, error.response?.data);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to assign sales rep'
      };
    }
  },

  bulkAssignSalesRep: async (customerIds, salesRepId) => {
    try {
      const response = await apiClient.post('/customers/assignments/sales/create/', {
        sales_rep_id: salesRepId,
        customer_ids: customerIds
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to assign sales rep'
      };
    }
  },

  bulkAssignAccountManager: async (customerIds, accountManagerId) => {
    try {
      const response = await apiClient.post('/customers/assignments/account-managers/create/', {
        account_manager_id: accountManagerId,
        customer_ids: customerIds
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to assign account manager'
      };
    }
  },

  bulkRemoveAllSalesReps: async (customerIds) => {
    try {
      const response = await apiClient.post('/customers/assignments/sales/remove/', {
        customer_ids: customerIds
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to remove sales reps'
      };
    }
  },

  bulkRemoveAllAccountManagers: async (customerIds) => {
    try {
      const response = await apiClient.post('/customers/assignments/account-managers/remove/', {
        customer_ids: customerIds
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to remove account managers'
      };
    }
  },

  removeSalesRep: async (customerId, salesRepId) => {
    try {
      const response = await apiClient.post('/customers/assignments/sales/remove/', {
        sales_rep_id: salesRepId,
        customer_id: customerId
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to remove sales rep'
      };
    }
  },

  assignCustomerManager: async (customerId, managerId) => {
    try {
      const response = await apiClient.post('/customers/assignments/customers/create/', {
        customer_manager_id: managerId,
        shop_ids: [customerId]
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to assign manager'
      };
    }
  },

  removeCustomerManager: async (customerId, managerId) => {
    try {
      const response = await apiClient.post('/customers/assignments/customers/remove/', {
        manager_user_id: managerId,
        shop_ids: [customerId]
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to remove manager'
      };
    }
  },

  getAvailableAccountManagers: async () => {
    try {
      const response = await apiClient.get('/customers/users/roles/accountmanager/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch account managers'
      };
    }
  },

  assignAccountManager: async (customerId, accountManagerId) => {
    try {
      const response = await apiClient.post('/customers/assignments/account-managers/create/', {
        account_manager_id: accountManagerId,
        customer_ids: [customerId]
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to assign account manager'
      };
    }
  },

  removeAccountManager: async (customerId, accountManagerId) => {
    try {
      const response = await apiClient.post('/customers/assignments/account-managers/remove/', {
        account_manager_id: accountManagerId,
        customer_id: customerId
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to remove account manager'
      };
    }
  },

  getSegmentationSettings: async () => {
    try {
      const response = await apiClient.get('/customers/segmentation-settings/');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch segmentation settings'
      };
    }
  },

  updateSegmentationSettings: async (settings) => {
    try {
      const response = await apiClient.put('/customers/segmentation-settings/', settings);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update segmentation settings'
      };
    }
  },

  // Customer Groups
  getCustomerGroups: async () => {
    try {
      const response = await apiClient.get('/customers/groups/');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to fetch customer groups' };
    }
  },

  createCustomerGroup: async (groupData) => {
    try {
      const response = await apiClient.post('/customers/groups/', groupData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Failed to create customer group' };
    }
  },

  updateCustomerGroup: async (groupId, groupData) => {
    try {
      const response = await apiClient.put(`/customers/groups/${groupId}/`, groupData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Failed to update customer group' };
    }
  },

  // Classifications
  getClassifications: async () => {
    try {
      const response = await apiClient.get('/customers/classifications/');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to fetch classifications' };
    }
  },

  createClassification: async (data) => {
    try {
      const response = await apiClient.post('/customers/classifications/', data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Failed to create classification' };
    }
  },

  updateClassification: async (id, data) => {
    try {
      const response = await apiClient.patch(`/customers/classifications/${id}/`, data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Failed to update classification' };
    }
  },

  deleteClassification: async (id) => {
    try {
      await apiClient.delete(`/customers/classifications/${id}/`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Failed to delete classification' };
    }
  },

  bulkSetClassifications: async (customerIds, changes) => {
    try {
      const response = await apiClient.post('/customers/classifications/bulk-assign/', { customer_ids: customerIds, changes });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to update classifications' };
    }
  },

  createClassificationOption: async (data) => {
    try {
      const response = await apiClient.post('/customers/classification-options/', data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Failed to create option' };
    }
  },

  updateClassificationOption: async (id, data) => {
    try {
      const response = await apiClient.patch(`/customers/classification-options/${id}/`, data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Failed to update option' };
    }
  },

  deleteClassificationOption: async (id) => {
    try {
      await apiClient.delete(`/customers/classification-options/${id}/`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Failed to delete option' };
    }
  },

  // Sales Zones
  getSalesZones: async () => {
    try {
      const response = await apiClient.get('/customers/sales-zones/');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to fetch sales zones' };
    }
  },

  createSalesZone: async (zoneData) => {
    try {
      const response = await apiClient.post('/customers/sales-zones/', zoneData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Failed to create sales zone' };
    }
  },

  updateSalesZone: async (zoneId, zoneData) => {
    try {
      const response = await apiClient.put(`/customers/sales-zones/${zoneId}/`, zoneData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Failed to update sales zone' };
    }
  },

  deleteSalesZone: async (zoneId) => {
    try {
      await apiClient.delete(`/customers/sales-zones/${zoneId}/`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Failed to delete sales zone' };
    }
  },

  // Lifecycle Stages
  getLifecycleStages: async (includeInactive = false) => {
    try {
      const response = await apiClient.get(`/customers/lifecycle-stages/?include_inactive=${includeInactive}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to fetch lifecycle stages' };
    }
  },

  createLifecycleStage: async (stageData) => {
    try {
      const response = await apiClient.post('/customers/lifecycle-stages/', stageData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Failed to create lifecycle stage' };
    }
  },

  updateLifecycleStage: async (stageId, stageData) => {
    try {
      const response = await apiClient.put(`/customers/lifecycle-stages/${stageId}/`, stageData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Failed to update lifecycle stage' };
    }
  },

  deleteLifecycleStage: async (stageId) => {
    try {
      await apiClient.delete(`/customers/lifecycle-stages/${stageId}/`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to delete lifecycle stage' };
    }
  },

  initLifecycleStages: async () => {
    try {
      const response = await apiClient.post('/customers/lifecycle-stages/init/');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to initialize lifecycle stages' };
    }
  },

  reorderLifecycleStages: async (orderData) => {
    try {
      const response = await apiClient.post('/customers/lifecycle-stages/reorder/', { order: orderData });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to reorder lifecycle stages' };
    }
  },

  // ABC Tiers
  getAbcTiers: async (includeInactive = false) => {
    try {
      const response = await apiClient.get(`/customers/abc-tiers/?include_inactive=${includeInactive}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to fetch ABC tiers' };
    }
  },

  createAbcTier: async (tierData) => {
    try {
      const response = await apiClient.post('/customers/abc-tiers/', tierData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Failed to create ABC tier' };
    }
  },

  updateAbcTier: async (tierId, tierData) => {
    try {
      const response = await apiClient.put(`/customers/abc-tiers/${tierId}/`, tierData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data || 'Failed to update ABC tier' };
    }
  },

  deleteAbcTier: async (tierId) => {
    try {
      await apiClient.delete(`/customers/abc-tiers/${tierId}/`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to delete ABC tier' };
    }
  },

  initAbcTiers: async () => {
    try {
      const response = await apiClient.post('/customers/abc-tiers/init/');
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to initialize ABC tiers' };
    }
  },

  reorderAbcTiers: async (orderData) => {
    try {
      const response = await apiClient.post('/customers/abc-tiers/reorder/', { order: orderData });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to reorder ABC tiers' };
    }
  }
};